import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateSignupForm } from "../utils/formValidation";
import { Box, Button, IconButton, Link, useTheme } from "@mui/material";
import CustomLabel from "../components/CustomLabel";
import CustomTextField from "../components/CustomTextField";
import { COLORS } from "../assets/theme";
import CountryCodePhoneField from "../components/CountryCodePhoneField";
import { signup } from "../services/authService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const Signup: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{
    fullName?: string;
    email?: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const validationResults = validateSignupForm({
      fullName,
      email,
      phone: phoneNumber,
      password,
      confirmPassword,
    });
    const newErrors: { [key: string]: string } = {};
    Object.entries(validationResults).forEach(([key, result]) => {
      if (!result.valid && result.error) {
        newErrors[key] = result.error;
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      const result = await signup(
        fullName,
        email,
        password,
        phoneNumber,
        countryCode
      );

      if (result.success) {
        const successMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof (result as any)?.user?.message === "string" &&
            (result as any).user.message.trim()) ||
          (typeof (result as any)?.user?.msg === "string" &&
            (result as any).user.msg.trim()) ||
          "Account created successfully!";
        toast.success(successMessage, {
          position: "top-right",
        });
        navigate("/home");
      } else {
        const errorMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof result.error === "string" && result.error.trim()) ||
          result.error ||
          "Signup failed";
        toast.error(errorMessage, {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("An unexpected error occurred", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  function onCountryCodeChange(value: string): void {
    setCountryCode(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  function onPhoneNumberChange(value: string): void {
    setPhoneNumber(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", sm: 400, md: 500 },
          maxWidth: "95vw",
          minHeight: { xs: 400, sm: 500, md: 600 },
          bgcolor: COLORS.WhiteBackground,
          borderRadius: 4,
          boxShadow: 3,
          p: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <CustomLabel
          text="Sign Up"
          fontWeight={700}
          fontSize="2rem"
          color={COLORS.BlackText}
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            borderBottom: `2px solid ${COLORS.BlackText}`,
            mb: 3,
            width: "80%",
          }}
        />
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
          }}
        >
          <CustomLabel
            text="Full Name"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type="text"
            value={fullName}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setFullName(e.target.value);
              setErrors((prev) => ({ ...prev, fullName: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
          <CustomLabel
            text="Email"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type="email"
            value={email}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.email}
            helperText={errors.email}
          />
          <CountryCodePhoneField
            countryCode={countryCode}
            phoneNumber={phoneNumber}
            onCountryCodeChange={onCountryCodeChange}
            onPhoneNumberChange={onPhoneNumberChange}
            error={!!errors.phone}
          />
          <CustomLabel
            text="Password"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type={showPassword ? "text" : "password"}
            value={password}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.password}
            helperText={errors.password}
            endIcon={
              <IconButton
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
          <CustomLabel
            text="Confirm Password"
            fontWeight={400}
            fontSize="16px"
            color={COLORS.BlackText}
            sx={{ mb: 1 }}
          />
          <CustomTextField
            label=""
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            bgColor={COLORS.textFieldBackgroundColor}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
            }}
            required
            fullWidth
            sx={{ mb: 2 }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            endIcon={
              <IconButton
                aria-label={
                  showConfirmPassword ? "Hide confirm password" : "Show confirm password"
                }
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              borderRadius: 30,
              py: 1.5,
              fontWeight: 700,
              fontSize: "1.1rem",
              boxShadow: "0 2px 8px rgba(44, 130, 201, 0.12)",
              transition: "background 0.2s",
              background: loading
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              "&:hover": { background: theme.palette.primary.dark },
              mt: 2,
            }}
          >
            <CustomLabel
              text={loading ? "Signing up..." : "Sign Up"}
              fontWeight={700}
              fontSize="1.1rem"
              color="#fff"
            />
          </Button>
        </Box>
        <CustomLabel
          text="Already have an account?"
          fontWeight={400}
          fontSize="1rem"
          color={COLORS.BlackText}
          sx={{ textAlign: "center", mt: 3 }}
        />
        <Link
          href="/login"
          underline="hover"
          sx={{
            fontWeight: 700,
            color: COLORS.BlackText,
            display: "block",
            textAlign: "center",
            mb: 2,
          }}
        >
          Login
        </Link>
      </Box>
    </Box>
  );
};

export default Signup;
